import moment from "moment";

export const MEDIA_DATE_MIN_YEAR = 1900;

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

export type MediaDateValidation =
	| { isValid: true; error: null; isoValue: string }
	| { isValid: false; error: string | null; isoValue: null };

function invalid(error: string | null): MediaDateValidation {
	return { isValid: false, error, isoValue: null };
}

export function validateMediaEditDate(datePart: string, timePart: string, now: Date = new Date()): MediaDateValidation {
	if (!datePart && !timePart) {
		// Both blank = the dialog has not been populated yet; stay silent so nothing flashes.
		return invalid(null);
	}

	// LSP-16407: one blank part with the other filled means the user cleared a segment
	// (e.g. backspacing the minutes of the native time input clears its whole value while
	// the hours still look filled in). Silently disabling Save here left no way to tell
	// why saving was blocked, so surface an actionable message instead.
	if (!timePart) {
		return invalid("Please enter a complete time.");
	}
	if (!datePart) {
		return invalid("Please enter a complete date.");
	}

	// Tolerate callers passing a full ISO string for the date part (e.g. "2025-06-01T00:00:00Z").
	const dateOnly = datePart.split("T")[0];
	const picked = moment(`${dateOnly} ${timePart}`, `${DATE_FORMAT} ${TIME_FORMAT}`, true);
	if (!picked.isValid()) {
		return invalid("Please pick a valid date.");
	}

	// Surface the specific min-year message; sub-1900 years would otherwise quietly pass the
	// "no later than today" check (they're all in the past) and the user would never learn why.
	if (picked.year() < MEDIA_DATE_MIN_YEAR) {
		return invalid(`Please pick a date from ${MEDIA_DATE_MIN_YEAR} or later.`);
	}

	const nowMoment = moment(now);
	if (picked.isAfter(nowMoment, "day")) {
		return invalid("Please pick a date no later than today.");
	}

	if (picked.isAfter(nowMoment)) {
		return invalid("Please pick a time that is not in the future.");
	}

	return { isValid: true, error: null, isoValue: picked.format(`${DATE_FORMAT}T${TIME_FORMAT}`) };
}
